import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Space, Tag, message } from 'antd';
import {
    GiftOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { AxiosError } from 'axios';
import { ColumnsType, SortOrder } from 'antd/es/table/interface';

import { IVoucher } from '../../interfaces';
import useAuth from '../../hooks/useAuth';
import VouchersService from '../../services/http/services/VouchersService';
import { EmitError } from '../../utils/EmitError';
import { formatCpf, onlyNumbers } from '../../utils/Masks';
import { formatDateStringToDate, formatDateStringToDateAndTime } from '../../utils/Conversores';
import { removeAcento } from '../../utils/RemoveAcentos';
import { useGetColumnSearchPropsData } from '../../hooks/FilterTable/date';
import { ThemeContext } from '../../contexts/ThemeContext';
import TableActionButton from '../../components/TableActionButton';
import { IActiveFilterItem } from '../../components/ActiveFiltersBar';
import { findChunksCpfCnpj, findChunksText, HIGHLIGHT_STYLE } from '../../utils/Highlight';
import useTableColumnFilter from '../../hooks/useTableColumnFilter';

export interface ITableData extends IVoucher {
    key: number;
    app_user_name: string;
    app_user_cpf: string;
    reward_name: string;
}

export interface IVoucherFilters {
    name: string | null;
    cpf: string | null;
    reward_name: string | null;
    redemption_code: string | null;
    created_at: string | null;
    date_redeemed: string | null;
    validity: string | null;
    status: string | null;
}

export type SortField =
    | 'app_user_name'
    | 'reward_name'
    | 'redemption_code'
    | 'points_used'
    | 'created_at'
    | 'date_redeemed'
    | 'validity'
    | 'status'
    | null;

export default function useVouchers() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const { activeCity } = useAuth();
    const { getColumnSearchProps, getColumnRadioProps } = useTableColumnFilter();

    // Call custom date search hooks at the top level
    const createdDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'created_at' });
    const redeemedDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'date_redeemed' });
    const validityDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'validity' });

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<IVoucherFilters>({
        name: null,
        cpf: null,
        reward_name: null,
        redemption_code: null,
        created_at: null,
        date_redeemed: null,
        validity: null,
        status: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'created_at',
        order: 'desc',
    });

    const [searchCitizenName, setSearchCitizenName] = useState(filters.name || '');
    const [searchCitizenCpf, setSearchCitizenCpf] = useState(filters.cpf || '');
    const [searchRewardName, setSearchRewardName] = useState(filters.reward_name || '');
    const [searchRedemptionCode, setSearchRedemptionCode] = useState(filters.redemption_code || '');
    const [quickCodeInput, setQuickCodeInput] = useState('');

    const citizenNameInputRef = useRef<any>(null);
    const citizenCpfInputRef = useRef<any>(null);

    useEffect(() => {
        setSearchCitizenName(filters.name || '');
    }, [filters.name]);

    useEffect(() => {
        setSearchCitizenCpf(filters.cpf || '');
    }, [filters.cpf]);

    useEffect(() => {
        setSearchRewardName(filters.reward_name || '');
    }, [filters.reward_name]);

    useEffect(() => {
        setSearchRedemptionCode(filters.redemption_code || '');
    }, [filters.redemption_code]);

    const handleFilterChange = useCallback((key: keyof IVoucherFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleSearchCitizen = useCallback((confirm?: () => void) => {
        setFilters((prev) => ({
            ...prev,
            name: searchCitizenName.trim() || null,
            cpf: searchCitizenCpf.trim() || null,
        }));
        setPagination((prev) => ({ ...prev, current: 1 }));
        if (confirm) confirm();
    }, [searchCitizenName, searchCitizenCpf]);

    const handleClearCitizen = useCallback((clearFilters?: () => void, confirm?: () => void) => {
        setSearchCitizenName('');
        setSearchCitizenCpf('');
        setFilters((prev) => ({
            ...prev,
            name: null,
            cpf: null,
        }));
        if (clearFilters) clearFilters();
        if (confirm) confirm();
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            name: null,
            cpf: null,
            reward_name: null,
            redemption_code: null,
            created_at: null,
            date_redeemed: null,
            validity: null,
            status: null,
        });
        setSearchCitizenName('');
        setSearchCitizenCpf('');
        setSearchRewardName('');
        setSearchRedemptionCode('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const setTableDataFromRequest = useCallback((data: Array<IVoucher>) => {
        if (!Array.isArray(data)) return;
        const newData: ITableData[] = data.map((voucher) => ({
            ...voucher,
            key: voucher.id,
            app_user_name: voucher.app_user?.name || '',
            app_user_cpf: voucher.app_user?.cpf || '',
            reward_name: voucher?.reward?.name || '',
        }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        if (!activeCity) return;

        const controller = new AbortController();

        async function loadVouchers() {
            try {
                setIsLoading(true);
                const vouchersList = await VouchersService.listVouchers({
                    cityId: activeCity?.id!,
                    signal: controller.signal,
                });
                setTableDataFromRequest(vouchersList);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'vouchers_list_error');
            } finally {
                setIsLoading(false);
            }
        }

        loadVouchers();

        return () => {
            controller.abort();
        };
    }, [activeCity, setTableDataFromRequest]);

    const filteredTableData = useMemo(() => {
        let result = rawData;

        if (filters.name) {
            const query = removeAcento(filters.name.trim().toLowerCase());
            result = result.filter((item) =>
                item.app_user_name ? removeAcento(item.app_user_name.toLowerCase()).includes(query) : false
            );
        }

        if (filters.cpf) {
            const query = onlyNumbers(filters.cpf);
            result = result.filter((item) => {
                const raw = onlyNumbers(item.app_user_cpf || '');
                return raw.includes(query);
            });
        }

        if (filters.reward_name) {
            const query = removeAcento(filters.reward_name.trim().toLowerCase());
            result = result.filter((item) =>
                item.reward_name ? removeAcento(item.reward_name.toLowerCase()).includes(query) : false
            );
        }

        if (filters.redemption_code) {
            const query = filters.redemption_code.trim().toUpperCase();
            result = result.filter((item) =>
                (item.redemption_code || '').toUpperCase().includes(query)
            );
        }

        if (filters.created_at) {
            const query = filters.created_at.trim();
            result = result.filter((item) => {
                if (!item.created_at) return false;
                const formatted = formatDateStringToDate(item.created_at);
                return formatted.includes(query);
            });
        }

        if (filters.date_redeemed) {
            const query = filters.date_redeemed.trim();
            result = result.filter((item) => {
                if (!item.date_redeemed) return false;
                const formatted = formatDateStringToDate(item.date_redeemed);
                return formatted.includes(query);
            });
        }

        if (filters.validity) {
            const query = filters.validity.trim();
            result = result.filter((item) => {
                if (!item.validity) return false;
                const formatted = formatDateStringToDate(item.validity);
                return formatted.includes(query);
            });
        }

        if (filters.status) {
            result = result.filter((item) => item.status === filters.status);
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'app_user_name') {
                    const comp = (a.app_user_name || '').localeCompare(b.app_user_name || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'reward_name') {
                    const comp = (a.reward_name || '').localeCompare(b.reward_name || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'redemption_code') {
                    const comp = (a.redemption_code || '').localeCompare(b.redemption_code || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'points_used') {
                    const valA = a.points_used || 0;
                    const valB = b.points_used || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'created_at') {
                    const valA = new Date(a.created_at || 0).getTime();
                    const valB = new Date(b.created_at || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'date_redeemed') {
                    const valA = new Date(a.date_redeemed || 0).getTime();
                    const valB = new Date(b.date_redeemed || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'validity') {
                    const valA = new Date(a.validity || 0).getTime();
                    const valB = new Date(b.validity || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'status') {
                    const comp = (a.status || '').localeCompare(b.status || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter]);

    const activeFilterItems: Array<IActiveFilterItem | null> = useMemo(() => [
        filters.name
            ? {
                key: 'name',
                label: 'Cidadão',
                value: filters.name,
                onClear: () => handleFilterChange('name', null),
            }
            : null,
        filters.cpf
            ? {
                key: 'cpf',
                label: 'CPF',
                value: formatCpf(filters.cpf),
                onClear: () => handleFilterChange('cpf', null),
            }
            : null,
        filters.reward_name
            ? {
                key: 'reward_name',
                label: 'Recompensa',
                value: filters.reward_name,
                onClear: () => {
                    handleFilterChange('reward_name', null);
                    setSearchRewardName('');
                },
            }
            : null,
        filters.redemption_code
            ? {
                key: 'redemption_code',
                label: 'Código de Resgate',
                value: filters.redemption_code,
                onClear: () => {
                    handleFilterChange('redemption_code', null);
                    setSearchRedemptionCode('');
                },
            }
            : null,
        filters.created_at
            ? {
                key: 'created_at',
                label: 'Data de Resgate',
                value: formatDateStringToDate(filters.created_at),
                onClear: () => handleFilterChange('created_at', null),
            }
            : null,
        filters.date_redeemed
            ? {
                key: 'date_redeemed',
                label: 'Data de Entrega',
                value: formatDateStringToDate(filters.date_redeemed),
                onClear: () => handleFilterChange('date_redeemed', null),
            }
            : null,
        filters.validity
            ? {
                key: 'validity',
                label: 'Validade',
                value: formatDateStringToDate(filters.validity),
                onClear: () => handleFilterChange('validity', null),
            }
            : null,
        filters.status
            ? {
                key: 'status',
                label: 'Status',
                value: filters.status,
                onClear: () => handleFilterChange('status', null),
            }
            : null,
    ], [filters, handleFilterChange]);

    const getSortOrder = useCallback((field: SortField): SortOrder | undefined => {
        if (sorter.field === field) {
            return sorter.order === 'asc' ? 'ascend' : sorter.order === 'desc' ? 'descend' : undefined;
        }
        return undefined;
    }, [sorter]);

    const handleTableChange = (_: any, __: any, newSorter: any) => {
        if (newSorter && newSorter.field) {
            setSorter({
                field: newSorter.field as SortField,
                order: newSorter.order === 'ascend' ? 'asc' : newSorter.order === 'descend' ? 'desc' : null,
            });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const handleQuickCodeSubmit = useCallback((e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const clean = quickCodeInput.replace(/\D/g, '').trim();
        if (clean.length !== 6) {
            message.warning('Por favor, informe um código de voucher válido com 6 dígitos.');
            return;
        }
        navigate(`/vouchers/${clean}`);
    }, [quickCodeInput, navigate]);

    const columns: ColumnsType<ITableData> = useMemo(() => [
        {
            title: 'Cidadão',
            dataIndex: 'app_user_name',
            key: 'app_user_name',
            sorter: true,
            sortOrder: getSortOrder('app_user_name'),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 280, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Buscar por Nome
                        </div>
                        <Input
                            ref={citizenNameInputRef}
                            placeholder="Digite o nome..."
                            value={searchCitizenName}
                            onChange={(e) => setSearchCitizenName(e.target.value)}
                            onPressEnter={() => handleSearchCitizen(confirm)}
                            allowClear
                        />
                    </div>

                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Buscar por CPF
                        </div>
                        <Input
                            ref={citizenCpfInputRef}
                            placeholder="000.000.000-00"
                            value={searchCitizenCpf}
                            onChange={(e) => setSearchCitizenCpf(formatCpf(e.target.value))}
                            onPressEnter={() => handleSearchCitizen(confirm)}
                            allowClear
                        />
                    </div>

                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => handleSearchCitizen(confirm)}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => handleClearCitizen(clearFilters, confirm)}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (filters.name || filters.cpf) ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => citizenNameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span
                        style={{
                            fontWeight: 600,
                            color: isDark ? '#f0f0f0' : '#1e293b',
                            fontSize: 13,
                            lineHeight: '1.3',
                        }}
                    >
                        {filters.name ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[filters.name]}
                                textToHighlight={record.app_user_name || '--'}
                                findChunks={findChunksText}
                            />
                        ) : (
                            record.app_user_name || '--'
                        )}
                    </span>
                    <span style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b', fontFamily: 'monospace' }}>
                        {filters.cpf ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[filters.cpf]}
                                textToHighlight={formatCpf(record.app_user_cpf)}
                                findChunks={findChunksCpfCnpj}
                            />
                        ) : (
                            formatCpf(record.app_user_cpf)
                        )}
                    </span>
                </div>
            ),
        },
        {
            title: 'Recompensa',
            dataIndex: 'reward_name',
            key: 'reward_name',
            sorter: true,
            sortOrder: getSortOrder('reward_name'),
            showSorterTooltip: false,
            ...getColumnSearchProps({
                title: 'Buscar Recompensa',
                placeholder: 'Nome da recompensa...',
                value: searchRewardName,
                onChange: setSearchRewardName,
                onSearch: (val) => handleFilterChange('reward_name', val),
                isActive: Boolean(filters.reward_name),
            }),
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GiftOutlined style={{ color: '#1677ff', fontSize: 15 }} />
                    <span style={{ fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b' }}>
                        {filters.reward_name ? (
                            <Highlighter
                                highlightStyle={HIGHLIGHT_STYLE}
                                searchWords={[filters.reward_name]}
                                textToHighlight={record.reward_name || '--'}
                                findChunks={findChunksText}
                            />
                        ) : (
                            record.reward_name || '--'
                        )}
                    </span>
                </div>
            ),
        },
        {
            title: 'Código',
            dataIndex: 'redemption_code',
            key: 'redemption_code',
            sorter: true,
            sortOrder: getSortOrder('redemption_code'),
            showSorterTooltip: false,
            ...getColumnSearchProps({
                title: 'Buscar Código',
                placeholder: 'Código de 6 dígitos...',
                value: searchRedemptionCode,
                onChange: setSearchRedemptionCode,
                onSearch: (val) => handleFilterChange('redemption_code', val),
                isActive: Boolean(filters.redemption_code),
            }),
            render: (code: string) => (
                <span
                    style={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: 13,
                        color: '#1677ff',
                        background: isDark ? 'rgba(22, 119, 255, 0.12)' : '#e6f4ff',
                        padding: '3px 8px',
                        borderRadius: 6,
                        letterSpacing: 1,
                    }}
                >
                    {filters.redemption_code ? (
                        <Highlighter
                            highlightStyle={HIGHLIGHT_STYLE}
                            searchWords={[filters.redemption_code]}
                            textToHighlight={code}
                            findChunks={findChunksText}
                        />
                    ) : (
                        code
                    )}
                </span>
            ),
        },
        {
            title: 'Pontos',
            dataIndex: 'points_used',
            key: 'points_used',
            align: 'right' as const,
            sorter: true,
            sortOrder: getSortOrder('points_used'),
            showSorterTooltip: false,
            render: (points: number) => (
                <Tag
                    color="blue"
                    style={{
                        margin: 0,
                        borderRadius: 12,
                        fontWeight: 700,
                        fontSize: 12,
                    }}
                >
                    {(points || 0).toLocaleString('pt-BR')} P
                </Tag>
            ),
        },
        {
            title: 'Data de Resgate',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: true,
            sortOrder: getSortOrder('created_at'),
            showSorterTooltip: false,
            ...createdDateSearchProps,
            render: (date: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {date ? formatDateStringToDateAndTime(date) : '--'}
                </span>
            ),
        },
        {
            title: 'Data de Entrega',
            dataIndex: 'date_redeemed',
            key: 'date_redeemed',
            sorter: true,
            sortOrder: getSortOrder('date_redeemed'),
            showSorterTooltip: false,
            ...redeemedDateSearchProps,
            render: (date: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {date ? formatDateStringToDateAndTime(date) : '--'}
                </span>
            ),
        },
        {
            title: 'Validade',
            dataIndex: 'validity',
            key: 'validity',
            sorter: true,
            sortOrder: getSortOrder('validity'),
            showSorterTooltip: false,
            ...validityDateSearchProps,
            render: (date: any, record: ITableData) => {
                const isExpired = record.status === 'Expirado' || (date && new Date(date).getTime() < Date.now() && record.status === 'Emitido');
                return (
                    <span
                        style={{
                            fontSize: 12.5,
                            whiteSpace: 'nowrap',
                            fontWeight: isExpired ? 600 : 400,
                            color: isExpired ? '#ef4444' : isDark ? '#cbd5e1' : '#475569',
                        }}
                    >
                        {date ? formatDateStringToDateAndTime(date) : '--'}
                    </span>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            sortOrder: getSortOrder('status'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Status',
                value: filters.status || 'ALL',
                onChange: (val) => handleFilterChange('status', val === 'ALL' ? null : val),
                isActive: Boolean(filters.status),
                options: [
                    { label: 'Todos', value: 'ALL' },
                    { label: 'Emitido', value: 'Emitido' },
                    { label: 'Resgatado', value: 'Resgatado' },
                    { label: 'Expirado', value: 'Expirado' },
                ],
            }),
            render: (status: string) => {
                const color = status === 'Resgatado' ? 'success' : status === 'Expirado' ? 'error' : 'processing';
                return (
                    <Tag
                        color={color}
                        style={{
                            borderRadius: 6,
                            fontWeight: 600,
                            padding: '2px 8px',
                        }}
                    >
                        {status}
                    </Tag>
                );
            },
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 70,
            render: (_: any, record: ITableData) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar detalhes do voucher"
                    onClick={() => navigate(`/vouchers/${record.redemption_code}`)}
                />
            ),
        },
    ], [
        isDark,
        filters,
        getSortOrder,
        searchCitizenName,
        searchCitizenCpf,
        searchRewardName,
        searchRedemptionCode,
        getColumnSearchProps,
        getColumnRadioProps,
        createdDateSearchProps,
        redeemedDateSearchProps,
        validityDateSearchProps,
        handleFilterChange,
        handleSearchCitizen,
        handleClearCitizen,
        navigate,
    ]);

    return {
        isLoading,
        columns,
        tableData: filteredTableData,
        pagination,
        activeFilterItems,
        quickCodeInput,
        setQuickCodeInput,
        handleQuickCodeSubmit,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        navigate,
    };
}
